import { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Heading2, Heading3, List, Quote, Link2, Pilcrow } from "lucide-react";

const BTN = "h-9 w-9 inline-flex items-center justify-center rounded-lg text-brand-slate hover:bg-brand-accent hover:text-brand-forest transition-colors";

export default function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);

  useEffect(() => {
    const clean = DOMPurify.sanitize(value || "");
    if (ref.current && ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const format = (tag) => exec("formatBlock", tag);

  const addLink = () => {
    const url = window.prompt("Enter the link URL (include https://)");
    if (url) exec("createLink", url);
  };

  return (
    <div className="rounded-2xl border border-brand-line bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-brand-line bg-brand-bg/60 px-2 py-1.5">
        <button type="button" className={BTN} title="Heading 2" onClick={() => format("H2")} data-testid="rte-h2"><Heading2 size={18} strokeWidth={1.5} /></button>
        <button type="button" className={BTN} title="Heading 3" onClick={() => format("H3")} data-testid="rte-h3"><Heading3 size={18} strokeWidth={1.5} /></button>
        <button type="button" className={BTN} title="Paragraph" onClick={() => format("P")} data-testid="rte-p"><Pilcrow size={18} strokeWidth={1.5} /></button>
        <span className="mx-1 h-5 w-px bg-brand-line" />
        <button type="button" className={BTN} title="Bold" onClick={() => exec("bold")} data-testid="rte-bold"><Bold size={18} strokeWidth={1.5} /></button>
        <button type="button" className={BTN} title="Italic" onClick={() => exec("italic")} data-testid="rte-italic"><Italic size={18} strokeWidth={1.5} /></button>
        <span className="mx-1 h-5 w-px bg-brand-line" />
        <button type="button" className={BTN} title="Bullet list" onClick={() => exec("insertUnorderedList")} data-testid="rte-ul"><List size={18} strokeWidth={1.5} /></button>
        <button type="button" className={BTN} title="Quote" onClick={() => format("BLOCKQUOTE")} data-testid="rte-quote"><Quote size={18} strokeWidth={1.5} /></button>
        <button type="button" className={BTN} title="Add link" onClick={addLink} data-testid="rte-link"><Link2 size={18} strokeWidth={1.5} /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid="rte-editor"
        className="rich min-h-[280px] max-h-[520px] overflow-y-auto px-5 py-4 focus:outline-none"
      />
    </div>
  );
}
